import { Format, NumberFormatter } from '@zervant/reactcomponents'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { ContactType, ExternalTaxRatesInterface } from 'client/Interfaces/ExternalVATRatesInterface'
import {
  cleanTaxRatesForRegion,
  getAllowedVatRateIdsByProductTypeAndCustomerTypeAndRegion,
  getAllowedVatRatesMap,
  getRegionFromCountry,
  getTaxRateByVatPct,
  getTaxRateByVatRateId,
} from 'client/Other/functions/externalTaxRateFunctions'
import { l } from 'client/Other/Translation'
import * as React from 'react'

import { putProductToLine } from '../../actions/products'
import { LineInterface, LineOrder } from '../../Interfaces/Draft/LineInterface'
import { ProductInterface, ProductTypesEnum } from '../../Interfaces/ProductInterface'
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface'
import { isDateStringValid, transformDateFormatForMoment } from '../../Other/functions/dateFunctions'
import { isZeroVatInvoiceType } from '../../Other/functions/invoiceLineFunctions'
import { convertPctFromLocale } from '../../Other/functions/vatFunctions'
import CheckBox from '../common/CheckBox/CheckBox'
import { CustomAutoComplete } from '../common/CustomAutoComplete/CustomAutoComplete'
import { DatePicker } from '../common/DatePicker/DatePicker'
import Input from '../common/Input/Input'
import { NumberInput } from '../common/NumberInput'
import { SelectInput } from '../common/SelectInput/SelectInput'

export interface InvoiceLinePropsInterface extends LineInterface {
  document: DocumentTypes
  onDelete?: (e: Event) => void
  onChange?: (values: LineInterface, fieldName?: string) => void
  onDrop?: (which: number) => void
  automation?: string
  columns?: Array<keyof LineInterface>
  discountFormat?: NumberFormatter
  currencyFormat?: NumberFormatter
  settings?: SettingsInterface
  id?: string
  index: number
  locale?: string
  readOnly?: boolean
  products?: any[]
  invalidFields?: string[]
  openProductModal?: (index) => void
  openDefaultProductTypeModal?: (index) => void
  openVATOptions: (next: (newVal) => void) => void
  openUnitOptions: (next: (newVal) => void) => void
  defaultProductType: ProductTypesEnum
  isAgerasNextMigration?: boolean
  customerCountry?: string | null
  customerType?: ContactType | null
  externalVATRates: ExternalTaxRatesInterface
  invoiceTypeId?: number
}

type LinePropsNames = keyof LineInterface

const LINE_PROP_NAMES: LinePropsNames[] = [
  'type',
  'grossTotal',
  'quantity',
  'description',
  'unitPrice',
  'netTotal',
  'taxTotal',
  'vatPct',
  'vatRateId',
  'unit',
  'productType',
  'discount',
  'defField',
  'date',
  'focused',
  'totalDiscount',
  'work',
]

export class InvoiceLine extends React.Component<InvoiceLinePropsInterface, LineInterface> {
  private currencyF: NumberFormatter
  private numberF: NumberFormatter
  private productTypesMap = {
    [ProductTypesEnum.goods]: l('productType.goods'),
    [ProductTypesEnum.services]: l('productType.services'),
  }

  constructor(props: InvoiceLinePropsInterface) {
    super(props)

    this.currencyF = this.props.currencyFormat
    this.numberF = new NumberFormatter(Format.fromLocale(props.locale))

    this.state = {
      dragOn: false,
      hasTypedDescription: false,
    }
  }

  private getLineProps() {
    const vatRateId = this.getVatRateId()
    const vatPct = this.getVatPctByVatRateId(vatRateId)
    const propValues = {
      ...this.props,
      ...(this.props?.isAgerasNextMigration ? { vatRateId, vatPct } : {}),
    }

    return Object.fromEntries(LINE_PROP_NAMES.map((propName: LinePropsNames) => [propName, propValues[propName]]))
  }

  public static defaultProps: Partial<InvoiceLinePropsInterface> = {
    description: '',
    grossTotal: 0,
    quantity: 0,
    unit: '',
    unitPrice: 0,
    vatPct: 0,
    netTotal: 0,
    taxTotal: 0,
    discount: 0,
    type: 'row',
    defField: 'PRICE',
    date: '',
    focused: false,
    locale: 'en_GB',
    totalDiscount: 0,
    readOnly: false,
    work: false,
  }

  private getVatRateId(originVatRateId?: string) {
    const { customerCountry, locale, externalVATRates, vatRateId, vatPct } = this.props
    let taxRate: { id: string; value: number }
    /**
     * Invoices created before ageras next migration have vatPct but not vatRateId
     */
    if (vatRateId) {
      taxRate = getTaxRateByVatRateId(
        externalVATRates,
        originVatRateId !== undefined ? originVatRateId : vatRateId,
        customerCountry,
        locale,
      )
    } else {
      taxRate = getTaxRateByVatPct(externalVATRates, vatPct, customerCountry, locale)
    }
    return taxRate?.id
  }

  public UNSAFE_componentWillReceiveProps(props: InvoiceLinePropsInterface) {
    if (props.currencyFormat) {
      this.currencyF = props.currencyFormat
    }
  }

  public componentDidMount(): void {
    this.updateVatRates()
  }

  public componentDidUpdate(prevProps) {
    this.updateVatRates(prevProps)
  }

  private updateVatRates(prevProps?: InvoiceLinePropsInterface) {
    const { vatRateId: prevVatRateId, vatPct: prevVatPct } = prevProps || this.props
    const isVatColumnVisible = this.props.columns?.includes('vatPct')
    const vatRateId = this.getVatRateId()
    const vatPct = this.getVatPctByVatRateId(vatRateId)

    if (!isVatColumnVisible) {
      return
    }

    if (this.props?.isAgerasNextMigration && (vatRateId !== prevVatRateId || vatPct !== prevVatPct)) {
      this.props.onChange?.({
        ...this.props,
        vatRateId,
        vatPct,
      })
    }
  }

  private canDrag: boolean = false

  private reactToDrag(elem: HTMLElement) {
    if (elem.getAttribute('data-type') === 'DragHandle') {
      this.canDrag = true
    }
  }

  private findDropTarget(elem: HTMLElement): HTMLElement {
    while (elem.getAttribute('data-type') !== 'DropTarget') {
      elem = elem.parentElement
    }
    return elem
  }

  private getVatPctByVatRateId(vatRateId) {
    const { customerCountry, locale, externalVATRates } = this.props
    const region = getRegionFromCountry(customerCountry, locale)

    return cleanTaxRatesForRegion(externalVATRates, region).find((taxRate) => taxRate.id == vatRateId)?.rate
  }

  private onVATRateChange(vatRateId, self) {
    const vatPct = this.getVatPctByVatRateId(vatRateId)
    self.props.onChange({ ...this.getLineProps(), vatPct, vatRateId })
  }

  private getMap(fieldName: string) {
    const map = {}
    this.props.settings?.managedFields[fieldName]?.map((name) => {
      map[name] = fieldName !== 'VAT' ? name : convertPctFromLocale(name, this.props.locale)
    })
    return map
  }

  private sendInvoiceLineChangeEventToSegment(name: string, value?: string | number) {
    if (name === 'date') {
      Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { date_column_value_changed: true })
    }
    if (name === 'unit') {
      Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { unit_column_value_changed: value || '' })
    }
    if (name === 'unitPrice') {
      Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { price_column_value_changed: true })
    }
    if (name === 'grossTotal') {
      Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { total_column_value_changed: true })
    }
    if (name === 'vatPct') {
      Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { vat_column_value_changed: value || '' })
    }
  }

  public render(): JSX.Element {
    const self = this
    const lineProps = this.getLineProps()
    const actions: JSX.Element[] = []
    const { customerCountry, externalVATRates, invoiceTypeId, locale, products, settings, vatPct } = this.props
    const taxRates = externalVATRates?.taxRates
    const customAPlaceHolder = {
      'data-automation': this.props.automation + '-description',
      placeholder: l('datagrid.product.Default'),
    }
    const dateFormat = transformDateFormatForMoment(this.props.settings?.values?.regional?.longDate)
    const showBillyVATRates = Boolean(this.props?.isAgerasNextMigration)
    const isVatRateSelectDisabled = isZeroVatInvoiceType(invoiceTypeId)

    if (this.props.onDelete && !this.props.readOnly) {
      actions.push(
        <span
          className="delete"
          key="onDelete"
          data-automation={this.props.automation + '-delete'}
          onClick={this.props.onDelete.bind(this)}
        ></span>,
      )
    }

    const columns = []
    for (const name of LineOrder) {
      if (this.props.columns.indexOf(name) < 0) {
        continue
      }
      let res = null
      switch (name) {
        case 'work':
          res = (
            <CheckBox
              value={this.props.work}
              automation={`${this.props.automation}-work`}
              onChange={(work: boolean) => {
                self.props.onChange({ ...lineProps, work }, name)
              }}
            />
          )
          break
        case 'description':
          res = (
            <CustomAutoComplete
              data={products}
              isReadOnly={this.props.readOnly}
              inputProps={{ ...customAPlaceHolder }}
              onChange={(e, description) => {
                if (!this.state.hasTypedDescription) {
                  Segment.track(SegmentEvents.invoiceLine.description, {
                    user_action: 'prod_name_typed',
                  })
                }
                self.props.onChange({ ...lineProps, description }, name)
                this.setState({ hasTypedDescription: true })
              }}
              onSelect={(e, product: ProductInterface) => {
                if (this.state.hasTypedDescription) {
                  Segment.track(SegmentEvents.invoiceLine.description, {
                    user_action: 'prod_name_typed_selected',
                  })
                } else {
                  Segment.track(SegmentEvents.invoiceLine.description, {
                    user_action: 'prod_selected',
                  })
                }
                if (this.props.isAgerasNextMigration) {
                  let productVatRateId: string | undefined = this.getVatRateId(product?.vatRateId)
                  const region = getRegionFromCountry(customerCountry, locale)
                  const allowedIds = getAllowedVatRateIdsByProductTypeAndCustomerTypeAndRegion(
                    externalVATRates?.rules,
                    product?.productType || this.props.productType,
                    this.props.customerType,
                    region,
                  )

                  if (allowedIds && !allowedIds.includes(productVatRateId)) {
                    productVatRateId = undefined
                  }

                  const productWithUpdatedVatRateAndVatPct = {
                    ...product,
                    vatRateId: productVatRateId,
                    vatPct: (productVatRateId && this.getVatPctByVatRateId(productVatRateId)) || 0,
                  }

                  self.props.onChange(putProductToLine(lineProps, productWithUpdatedVatRateAndVatPct), name)
                  return
                }

                self.props.onChange(putProductToLine(lineProps, product), name)
              }}
              value={this.props.description}
              addNewLabel={l('addProduct.AddProduct')}
              onAddNew={() => {
                this.props.openProductModal(this.props.index)
                Segment.track(SegmentEvents.invoiceLine.description, {
                  user_action: 'prod_created',
                })
              }}
            />
          )
          break
        case 'quantity':
          res = (
            <NumberInput
              formatter={this.numberF}
              automation={this.props.automation + '-quantity'}
              onBlur={(quantity) => {
                self.props.onChange({ ...lineProps, quantity }, name)
              }}
              value={this.props.quantity}
              decimals={2}
              minimum={0}
              align="left"
              editable={!this.props.readOnly}
              borderless={this.props.readOnly}
            />
          )
          break
        case 'unitPrice':
          res = (
            <NumberInput
              formatter={this.currencyF}
              automation={this.props.automation + '-unitPrice'}
              onBlur={(unitPrice) => {
                this.sendInvoiceLineChangeEventToSegment('unitPrice')
                self.props.onChange({ ...lineProps, unitPrice, defField: 'PRICE' }, name)
              }}
              value={this.props.unitPrice}
              decimals={2}
              align="right"
              editable={!this.props.readOnly}
              borderless={this.props.readOnly}
            />
          )
          break
        case 'vatPct':
          if (this.props.readOnly) {
            res = (
              <Input
                automation={this.props.automation + '-vatPct'}
                value={this.getMap('VAT')[vatPct || lineProps.vatPct]}
                editable={false}
                borderless={true}
              />
            )
          } else if (showBillyVATRates) {
            // if vatRateId is found in invalidFields, or no vatRateId in props
            const isVatColumnVisible = this.props.columns?.includes('vatPct')
            const noVatRate =
              isVatColumnVisible && (!lineProps.vatRateId || (!lineProps.vatPct && lineProps.vatPct !== 0))
            const invalidVAT = this.props.invalidFields?.includes(`line-${this.props.index}-vatRateId`) || noVatRate
            res = (
              <>
                <SelectInput
                  value={lineProps.vatRateId}
                  automation={this.props.automation + '-vatPct'}
                  searchable={false}
                  searchPlaceholder={l('search.Default')}
                  hasTitles={true}
                  map={getAllowedVatRatesMap(
                    externalVATRates,
                    customerCountry,
                    locale,
                    this.props.productType,
                    this.props.customerType,
                  )}
                  onChange={(value) => {
                    this.onVATRateChange(value, self)
                    this.sendInvoiceLineChangeEventToSegment('vatPct', taxRates.find((t) => t.id === value)?.rate)
                  }}
                  invalid={invalidVAT}
                  disabled={isVatRateSelectDisabled}
                />
                {invalidVAT && <div className="productTypeSelectErrorMsg">{l('editor.VATRateSelectError')}</div>}
              </>
            )
          } else {
            res = (
              <SelectInput
                value={lineProps.vatPct}
                automation={this.props.automation + '-vatPct'}
                searchable={false}
                searchPlaceholder={l('search.Default')}
                map={this.getMap('VAT')}
                onChange={(value) => {
                  this.sendInvoiceLineChangeEventToSegment('vatPct', value)
                  const vatPct = parseFloat(value)
                  self.props.onChange({ ...lineProps, vatPct }, name)
                }}
                disabled={isVatRateSelectDisabled}
              />
            )
          }
          break
        case 'taxTotal':
          res = (
            <NumberInput
              formatter={this.currencyF}
              borderless={true}
              editable={false}
              automation={this.props.automation + '-vat'}
              value={this.props.taxTotal}
              decimals={2}
            />
          )
          break
        case 'netTotal':
          res = (
            <NumberInput
              formatter={this.currencyF}
              borderless={true}
              editable={false}
              automation={this.props.automation + '-net'}
              value={this.props.netTotal}
              decimals={2}
            />
          )
          break
        case 'grossTotal':
          res = (
            <NumberInput
              formatter={this.currencyF}
              automation={this.props.automation + '-total'}
              onBlur={(grossTotal) => {
                this.sendInvoiceLineChangeEventToSegment('grossTotal')
                self.props.onChange({ ...lineProps, grossTotal, defField: 'GROSSTOTAL' }, name)
              }}
              value={this.props.grossTotal}
              decimals={2}
              editable={!this.props.readOnly}
              borderless={this.props.readOnly}
            />
          )
          break
        case 'unit':
          if (this.props.readOnly) {
            res = (
              <Input
                automation={this.props.automation + '-unit'}
                value={this.getMap('UNITS')[this.props.unit]}
                editable={false}
                borderless={true}
              />
            )
          } else {
            res = (
              <SelectInput
                value={this.props.unit}
                automation={this.props.automation + '-unit'}
                searchable={false}
                searchPlaceholder={l('search.Default')}
                map={this.getMap('UNITS')}
                onChange={(unit) => {
                  this.sendInvoiceLineChangeEventToSegment('unit', unit)
                  self.props.onChange({ ...lineProps, unit }, name)
                }}
                addNewLabel={l('input.select.EditValues')}
                onAddNew={() => {
                  Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { unit_column_new_value_added: true })
                  this.props.openUnitOptions((newUnit) => {
                    self.props.onChange({ ...lineProps, unit: newUnit }, name)
                  })
                }}
              />
            )
          }
          break
        case 'productType':
          if (this.props.document === DocumentTypes.ESTIMATE) {
            break
          }
          if (this.props.readOnly) {
            res = (
              <Input
                automation={this.props.automation + '-productType'}
                value={this.productTypesMap[this.props.productType]}
                editable={false}
                borderless={true}
              />
            )
          } else {
            const invalid = this.props.invalidFields?.includes(`line-${this.props.index}-productType`)
            res = (
              <>
                <SelectInput
                  value={this.props.productType}
                  automation={this.props.automation + '-productType'}
                  searchable={false}
                  searchPlaceholder={l('search.Default')}
                  map={this.productTypesMap}
                  onChange={(productType) => {
                    self.props.onChange({ ...lineProps, productType }, name)
                    this.props.openDefaultProductTypeModal(this.props.index)
                  }}
                  invalid={invalid}
                />
                {invalid && <div className="productTypeSelectErrorMsg">{l('editor.productTypeSelectError')}</div>}
              </>
            )
          }
          break
        case 'discount':
          res = (
            <NumberInput
              automation={this.props.automation + '-discount'}
              formatter={this.props.discountFormat}
              onBlur={(discount) => {
                self.props.onChange({ ...lineProps, discount }, name)
              }}
              value={this.props.discount}
              decimals={2}
              align="left"
              editable={!this.props.readOnly}
              borderless={this.props.readOnly}
            />
          )
          break
        case 'date':
          res = (
            <DatePicker
              automation={this.props.automation + '-date'}
              todayButton={l('Today')}
              value={this.props.date}
              onChange={(date) => {
                // If wrong date format we will use current time
                const timeNow = new Date().toISOString()
                const newDate = isDateStringValid(date) ? date : timeNow
                self.props.onChange({ ...lineProps, date: newDate }, name)
                this.sendInvoiceLineChangeEventToSegment('date')
              }}
              locale={this.props.locale}
              format={dateFormat}
              disabled={this.props.readOnly}
            />
          )
          break
        default:
          res = <span></span>
      }
      if (res) {
        columns.push(<td key={name}>{res}</td>)
      }
    }

    return (
      <tr
        className="Controlled InvoiceLine"
        data-type="DropTarget"
        draggable={!this.props.readOnly && this.state.dragOn}
        onDragOver={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement)
          elem.classList.add('Dropping')
          e.preventDefault()
        }}
        onDragLeave={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement)
          elem.classList.remove('Dropping')
          e.preventDefault()
        }}
        onDrop={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement)
          elem.classList.remove('Dropping')
          self.props.onDrop(parseInt(e.dataTransfer.getData('id'), 10))
          e.preventDefault()
        }}
        onDragStart={(e) => {
          if (!self.canDrag) {
            e.preventDefault()
            return false
          }
          e.dataTransfer.setData('id', '' + self.props.id)
        }}
        onDragEnd={(e) => {
          self.canDrag = false
        }}
        onMouseDown={(e) => {
          self.reactToDrag(e.target as HTMLElement)
        }}
        data-automation={this.props.automation}
      >
        <td
          data-automation={this.props.automation + '-dragHandle'}
          data-type="DragHandle"
          className="dragHandle"
          onMouseEnter={() => document.activeElement.blur()}
          onMouseOver={() => self.setState({ dragOn: true })}
          onMouseLeave={() => self.setState({ dragOn: false })}
        ></td>
        {columns}
        <td>{actions}</td>
      </tr>
    )
  }
}
